import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiDelete, apiGet, apiPost, apiPut } from "utils/apiFetch";
import apiPath from "utils/pathObj";
import useToastContext from "hooks/useToastContext";
import dayjs from "dayjs";
import IpLocationMap from "components/GoogleMap/IpLocationMap";
import { geocodeAddress, reverseGeocode } from "utils/googleMaps";
import ConfirmationModal from "components/reusable/ConfirmationModal";

const emptyOffice = { name: "", network: "", countryId: "", countryName: "", countryCode: "", stateId: "", stateName: "", stateCode: "", city: "", notes: "" };
const ROLE_SECTIONS = [
  { role: "companyAdmin", titleKey: "GEO_SECTION_COMPANY_ADMIN", helpKey: "GEO_SECTION_COMPANY_ADMIN_HELP" },
  // HR / accountant IPs live on HR onboarding and Accountant onboarding pages
  // { role: "HRAdmin", titleKey: "GEO_SECTION_HR", helpKey: "GEO_SECTION_HR_HELP" },
  // { role: "AccountantAdmin", titleKey: "GEO_SECTION_ACCOUNTANT", helpKey: "GEO_SECTION_ACCOUNTANT_HELP" },
//{ role: "employee", titleKey: "GEO_SECTION_EMPLOYEE", helpKey: "GEO_SECTION_EMPLOYEE_HELP" },
];

const EMP_PAGE_SIZE = 10;

function asHrEmployeeProfile(row) {
  const emp = row?.Employee || row || {};
  return {
    ...row,
    _id: emp._id || row.employeeID || row._id,
    name: emp.name || row.name,
    email: emp.email || row.email,
    employeeId: emp.employeeId || row.employeeId,
    city: emp.city || row.city,
    addressLine1: emp.addressLine1 || row.addressLine1,
    addressLine2: emp.addressLine2 || row.addressLine2,
    postalCode: emp.postalCode || row.postalCode,
    latitude: emp.latitude ?? row.latitude,
    longitude: emp.longitude ?? row.longitude,
    addressVerified: emp.addressVerified ?? row.addressVerified,
    countryName: row.EmployeeCountry?.name || emp.countryName || row.countryName,
    stateName: row.EmployeeState?.name || emp.stateName || row.stateName,
  };
}

function profileAddress(emp) {
  return (
    emp.storedAddress ||
    [emp.addressLine1, emp.addressLine2, emp.city, emp.stateName, emp.postalCode, emp.countryName]
      .filter(Boolean)
      .join(", ")
  );
}

function hasValidCoords(lat, lng) {
  if (lat == null || lng == null || lat === "" || lng === "") return false;
  const a = Number(lat);
  const b = Number(lng);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  if (a === 0 && b === 0) return false;
  return true;
}

function haversineKm(lat1, lng1, lat2, lng2) {
  const toRad = (n) => (n * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 6371 * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

// const verifyEmployeeOnClient = async (emp) => {
//   if (emp.addressStatus === "found" || emp.addressStatus === "incorrect") return emp;
//   const storedAddress = profileAddress(emp);
//   const lat = Number(emp.latitude);
//   const lng = Number(emp.longitude);
//   const hasCoords = hasValidCoords(emp.latitude, emp.longitude);
//   if (!storedAddress && !hasCoords) {
//     return { ...emp, addressStatus: "missing", addressCorrect: false, storedAddress: "" };
//   }

//   let fromText = null;
//   let fromPin = null;
//   if (storedAddress) {
//     try {
//       fromText = await geocodeAddress(storedAddress);
//     } catch (err) {
//       fromText = String(err?.message || "") === "ZERO_RESULTS" ? false : null;
//     }
//   }
//   if (hasCoords) {
//     try {
//       fromPin = await reverseGeocode(lat, lng);
//     } catch (err) {
//       fromPin = null;
//     }
//   }

//   if (fromText) {
//     let correct = true;
//     if (hasCoords) correct = haversineKm(lat, lng, fromText.latitude, fromText.longitude) <= 2;
//     return {
//       ...emp,
//       addressStatus: correct ? "found" : "incorrect",
//       addressCorrect: correct,
//       storedAddress,
//       realAddress: fromText.formattedAddress || fromPin?.formattedAddress || "",
//       realLatitude: fromText.latitude,
//       realLongitude: fromText.longitude,
//     };
//   }
//   if (fromPin) {
//     return {
//       ...emp,
//       addressStatus: storedAddress ? "incorrect" : "found",
//       addressCorrect: !storedAddress,
//       storedAddress,
//       realAddress: fromPin.formattedAddress || "",
//       realLatitude: lat,
//       realLongitude: lng,
//     };
//   }
//   if (fromText === false && storedAddress) {
//     return {
//       ...emp,
//       addressStatus: "incorrect",
//       addressCorrect: false,
//       storedAddress,
//       realAddress: "",
//       realLatitude: hasCoords ? lat : null,
//       realLongitude: hasCoords ? lng : null,
//     };
//   }
//   if (hasCoords) {
//     return {
//       ...emp,
//       addressStatus: "found",
//       addressCorrect: true,
//       storedAddress,
//       realLatitude: lat,
//       realLongitude: lng,
//     };
//   }
//   if (storedAddress) {
//     return {
//       ...emp,
//       addressStatus: "found",
//       addressCorrect: true,
//       storedAddress,
//       realAddress: storedAddress,
//     };
//   }
//   return emp;
// };

const GeographicAccess = () => {
  const { t } = useTranslation();
  const notification = useToastContext();
  const [policy, setPolicy] = useState(null);
  const [offices, setOffices] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logPage, setLogPage] = useState(1);
  const [logTotalPages, setLogTotalPages] = useState(1);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [officeForm, setOfficeForm] = useState(emptyOffice);
  const [editingOfficeId, setEditingOfficeId] = useState(null);
  const [ipDrafts, setIpDrafts] = useState({});
  const [blockValue, setBlockValue] = useState("");
  const [blockLabel, setBlockLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const [mapLoc, setMapLoc] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [empPage, setEmpPage] = useState(1);
  const [empTotalPages, setEmpTotalPages] = useState(1);
  const [empTotal, setEmpTotal] = useState(0);
  const [removeConfirm, setRemoveConfirm] = useState(null);
  const [enableConfirm, setEnableConfirm] = useState(null);

  const loadEmployees = async (page = 1) => {
    try {
      const empRes = await apiGet(apiPath.geoAccessEmployeeLocations, {
        page,
        pageSize: EMP_PAGE_SIZE,
      });
      if (empRes?.data?.success) {
        const docs = (empRes.data.results?.docs || []).map(asHrEmployeeProfile);
        setEmployees(docs);
        setEmpPage(empRes.data.results?.page || page);
        setEmpTotalPages(empRes.data.results?.totalPages || 1);
        setEmpTotal(empRes.data.results?.totalDocs || empRes.data.results?.total || docs.length);
        setSelectedEmployee((prev) => {
          if (prev && docs.some((d) => String(d._id) === String(prev._id))) {
            return docs.find((d) => String(d._id) === String(prev._id)) || prev;
          }
          return docs[0] || null;
        });
      }
    } catch (err) {
      console.log("employee locations", err?.message);
    }
  };

  const load = async () => {
    try {
      const [policyRes, officeRes, countryRes, logRes] = await Promise.all([
        apiGet(apiPath.geoAccessPolicy),
        apiGet(apiPath.geoAccessOffices, { page: 1, pageSize: 50 }),
        apiGet(apiPath.getCountryList, {}),
        apiGet(apiPath.geoAccessLogs, { page: 1, pageSize: 8 }),
      ]);
      if (policyRes?.data?.success) setPolicy(policyRes.data.results);
      else notification.error(policyRes?.data?.message || t("GEO_LOAD_FAILED"));
      if (officeRes?.data?.success) setOffices(officeRes.data.results?.docs || []);
      setCountries(countryRes?.data?.results?.allCountries || []);
      if (logRes?.data?.success) {
        setLogs(logRes.data.results?.docs || []);
        setLogPage(logRes.data.results?.page || 1);
        setLogTotalPages(logRes.data.results?.totalPages || 1);
      }
      try {
        await loadEmployees(1);
      } catch (err) {
        console.log("employee locations", err?.message);
      }
    } catch (err) {
      notification.error(err?.response?.data?.message || t("GEO_LOAD_FAILED"));
    }
  };

  useEffect(() => { load(); }, []);

  // Public IP is only used for "Allow my IP" on role lists — not for the map.
  useEffect(() => {
    if (!policy) return undefined;
    let cancelled = false;

    const setIf = (next) => {
      if (!cancelled) setMapLoc((prev) => ({ ...prev, ...next }));
    };

    const loadPublicIp = async () => {
      try {
        const ipRes = await fetch("https://api.ipify.org?format=json");
        const ipJson = await ipRes.json();
        const publicIp = String(ipJson?.ip || "").trim();
        if (!publicIp || cancelled) return;
        setIf({ ip: publicIp });
      } catch (err) {
        console.log("public IP fetch failed", err?.message);
      }
    };

    loadPublicIp();

    return () => {
      cancelled = true;
    };
  }, [policy]);
  /* Previous map used GPS / IP city. Super Admin + company now show employee profile pins instead.
  useEffect(() => {
    ... geolocation + ipapi map ...
  }, [policy, t]);
  */
  useEffect(() => {
    const run = async () => {
      if (!officeForm.countryId) { setStates([]); return; }
      try {
        const res = await apiGet(`${apiPath.getStateList}/${officeForm.countryId}`, {});
        setStates(res?.data?.results?.allStates || []);
      } catch (e) { setStates([]); }
    };
    run();
  }, [officeForm.countryId]);

  const savePolicy = async (partial) => {
    try {
      setSaving(true);
      const res = await apiPut(apiPath.geoAccessPolicy, { ...policy, ...partial });
      if (res?.data?.success) { setPolicy(res.data.results); notification.success(res.data.message || t("GEO_SAVED")); }
      else notification.error(res?.data?.message || t("GEO_SAVE_FAILED"));
    } catch (err) { notification.error(err?.response?.data?.message || t("GEO_SAVE_FAILED")); }
    setSaving(false);
  };

  const toggleEnabled = (checked) => {
    // PREVIOUS: turning ON auto-added currentIp + map public IP as companyAdmin
    // ("This computer"). Turning OFF left those rows, so the IP stayed in the list.
    // const nextAllow = [...(policy.ipAllowlist || [])];
    // const addIfMissing = (value, label) => {
    //   if (!value) return;
    //   if (nextAllow.some((i) => i.value === value && (i.role || "any") === "companyAdmin")) return;
    //   nextAllow.push({ value, label, role: "companyAdmin" });
    // };
    // if (checked) {
    //   addIfMissing(policy.currentIp, t("GEO_THIS_COMPUTER"));
    //   if (mapLoc?.ip && mapLoc.ip !== policy.currentIp) {
    //     addIfMissing(mapLoc.ip, t("GEO_PUBLIC_IP"));
    //   }
    // }
    // savePolicy({ enabled: checked, ipAllowlistOnly: checked, ipAllowlist: nextAllow });
    let nextAllow = [...(policy.ipAllowlist || [])];
    if (!checked) {
      const autoIps = [policy.currentIp, mapLoc?.ip].filter(Boolean);
      nextAllow = nextAllow.filter((entry) => {
        if ((entry.role || "any") !== "companyAdmin") return true;
        if (autoIps.includes(entry.value)) return false;
        return true;
      });
    }
    savePolicy({ enabled: checked, ipAllowlistOnly: checked, ipAllowlist: nextAllow });
  };

  const hasCompanyAdminIp = (policy?.ipAllowlist || []).some(
    (entry) => (entry.role || "any") === "companyAdmin" && entry.value,
  );

  const onToggleRestriction = () => {
    if (policy.enabled) {
      toggleEnabled(false);
      return;
    }
    if (!hasCompanyAdminIp) {
      setEnableConfirm({
        title: "Add company admin IP first",
        message:
          "You cannot enable geographic restriction until at least one company admin IP is added. Use Add IP or Allow my IP, then turn this on.",
        confirmText: "OK",
        hideCancel: true,
        run: null,
      });
      return;
    }
    setEnableConfirm({
      title: "Enable geographic restriction?",
      message:
        "A person can log in only when their IP is listed or they are on an office network. If the IP is not listed, login is blocked.",
      confirmText: "Yes, enable",
      hideCancel: false,
      run: () => toggleEnabled(true),
    });
  };

  const ipsFor = (role) => (policy?.ipAllowlist || []).filter((i) => (i.role || "any") === role);
  const addRoleIp = (role, value, label) => {
    const ip = (value || "").trim();
    if (!ip) return;
    const list = policy.ipAllowlist || [];
    if (list.some((i) => i.value === ip && (i.role || "any") === role)) { notification.error(t("GEO_IP_EXISTS")); return; }
    savePolicy({ ipAllowlist: [...list, { value: ip, label: (label || "").trim(), role }], ipBlocklist: (policy.ipBlocklist || []).filter((i) => i.value !== ip) });
  };
  const blockRoleIp = (role, value, label) => {
    const ip = (value || "").trim();
    if (!ip) return;
    const list = policy.ipBlocklist || [];
    if (list.some((i) => i.value === ip && (i.role || "any") === role)) { notification.error(t("GEO_IP_EXISTS")); return; }
    savePolicy({
      ipBlocklist: [...list, { value: ip, label: (label || "").trim() || t("GEO_SECTION_BLOCKED"), role }],
      ipAllowlist: (policy.ipAllowlist || []).filter((i) => !(i.value === ip && (i.role || "any") === role)),
    });
  };
  const removeBlockedIp = (value, role) => savePolicy({
    ipBlocklist: (policy.ipBlocklist || []).filter((i) => !(i.value === value && (!role || (i.role || "any") === role))),
  });
  const removeRoleIp = (role, value) => savePolicy({ ipAllowlist: (policy.ipAllowlist || []).filter((i) => !(i.value === value && (i.role || "any") === role)) });

  const submitOffice = async (e) => {
    e.preventDefault();
    const body = { name: officeForm.name, network: officeForm.network, countryName: officeForm.countryName, countryCode: officeForm.countryCode, stateName: officeForm.stateName, stateCode: officeForm.stateCode, city: officeForm.city, notes: officeForm.notes };
    const res = await (editingOfficeId ? apiPut(`${apiPath.geoAccessOffices}/${editingOfficeId}`, body) : apiPost(apiPath.geoAccessOffices, body));
    if (res?.data?.success) { notification.success(res.data.message); setOfficeForm(emptyOffice); setEditingOfficeId(null); load(); }
    else notification.error(res?.data?.message || t("GEO_SAVE_FAILED"));
  };
  const removeOffice = async (id) => {
    const res = await apiDelete(`${apiPath.geoAccessOffices}/${id}`);
    if (res?.data?.success) { notification.success(res.data.message); load(); }
    else notification.error(res?.data?.message || t("GEO_SAVE_FAILED"));
  };
  const loadLogs = async (page = 1) => {
    const res = await apiGet(apiPath.geoAccessLogs, { page, pageSize: 8 });
    if (res?.data?.success) { setLogs(res.data.results?.docs || []); setLogPage(res.data.results?.page || 1); setLogTotalPages(res.data.results?.totalPages || 1); }
  };

  if (!policy) return <div className="bg-white border border-[#E9EDF9] rounded-lg py-6 px-6 text-sm text-gray-500">{t("GEO_LOADING")}</div>;
  // Map no longer uses admin/company IP or GPS. Employee profile pins are shown instead.
  // const displayIp = mapLoc?.ip || policy.currentIp || "unknown";
  // const locLabel = mapLoc?.source === "gps" ? t("GEO_BROWSER_LOCATION") : (mapLoc?.ipLabel || mapLoc?.label || "");
  // const hasMap = Number.isFinite(Number(mapLoc?.latitude)) && Number.isFinite(Number(mapLoc?.longitude));
  // const mapZoom = mapLoc?.source === "gps" ? 16 : 11;

  return (
    <div className="bg-white border border-[#E9EDF9] rounded-lg py-6 px-6">
      <div className="mb-6">
        <h3 className="text-xl font-semibold mb-1">{t("GEOGRAPHIC_ACCESS")}</h3>
        <p className="text-sm text-gray-500 max-w-3xl">{t("GEO_COMPANY_HELP")}</p>
      </div>

        {/*  location and map  in grographic access*/}
      {/* <div className="mb-6">
        <h4 className="font-semibold mb-1">{t("GEO_EMPLOYEE_LOCATIONS")}</h4>
        <p className="text-sm text-gray-500 mb-3">{t("GEO_EMPLOYEE_LOCATIONS_HELP")}</p>
        <div className="grid lg:grid-cols-2 gap-4">
          <div>
            <div className="border rounded-lg overflow-hidden text-sm">
            {employees.length === 0 ? (
              <div className="p-3 text-gray-400">{t("GEO_NO_EMPLOYEE_LOCS")}</div>
            ) : (
              employees.map((emp) => {
                const address = profileAddress(emp);
                return (
                  <button
                    type="button"
                    key={emp._id}
                    onClick={() => setSelectedEmployee(emp)}
                    className={`w-full text-left px-3 py-2 border-b last:border-b-0 ${
                      selectedEmployee?._id === emp._id ? "bg-[#F3F6FF]" : "bg-white"
                    }`}
                  >
                    <div className="font-medium">{emp.name || "-"}</div>
                    <div className="text-xs text-gray-500">{emp.employeeId || "-"}</div>
                    <div className="text-xs text-gray-500">
                      {[emp.department, emp.jobTitle].filter(Boolean).join(" · ")}
                    </div>
                    <div className="text-xs text-gray-400">{emp.email || "-"}</div>
                    <div className="text-gray-500 mt-1">{address || t("GEO_NO_ADDRESS")}</div>
                  </button>
                );
              })
            )}
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <button
                type="button"
                className="border px-3 py-1 rounded disabled:opacity-40"
                disabled={empPage <= 1}
                onClick={() => loadEmployees(empPage - 1)}
              >
                {t("GEO_PREV")}
              </button>
              <span className="text-sm py-1">
                {t("GEO_PAGE")} {empPage} / {empTotalPages}
                {empTotal ? ` (${empTotal})` : ""}
              </span>
              <button
                type="button"
                className="border px-3 py-1 rounded disabled:opacity-40"
                disabled={empPage >= empTotalPages}
                onClick={() => loadEmployees(empPage + 1)}
              >
                {t("GEO_NEXT")}
              </button>
            </div>
          </div>
          <div>
            {selectedEmployee &&
            (hasValidCoords(selectedEmployee.latitude, selectedEmployee.longitude) ||
              profileAddress(selectedEmployee)) ? (
              <>
                <p className="text-sm mb-2 font-medium">
                  {selectedEmployee.name} {selectedEmployee.employeeId ? `(${selectedEmployee.employeeId})` : ""}
                </p>
                {profileAddress(selectedEmployee) ? (
                  <p className="text-xs text-gray-500 mb-2">{profileAddress(selectedEmployee)}</p>
                ) : null}
                <IpLocationMap
                  key={selectedEmployee._id}
                  latitude={Number(selectedEmployee.latitude)}
                  longitude={Number(selectedEmployee.longitude)}
                  query={profileAddress(selectedEmployee)}
                  label={profileAddress(selectedEmployee) || selectedEmployee.name}
                  height="240px"
                  zoom={15}
                />
              </>
            ) : (
              <div className="h-[240px] rounded-lg border border-[#E9EDF9] flex items-center justify-center text-sm text-gray-500 px-4 text-center">
                {t("GEO_SELECT_EMPLOYEE_MAP")}
              </div>
            )}
          </div>
        </div>
      </div> */}


{/* // company admin IP */}

      {/* <section className="mb-8">
        <h4 className="font-semibold mb-1">{t("GEO_SECTION_OFFICE")}</h4>
        <p className="text-sm text-gray-500 mb-3">{t("GEO_SECTION_OFFICE_HELP")}</p>
        <form onSubmit={submitOffice} className="grid md:grid-cols-3 gap-3 mb-4">
          <input className="border rounded-lg px-3 py-2" placeholder={t("GEO_OFFICE_NAME")} value={officeForm.name} onChange={(e) => setOfficeForm({ ...officeForm, name: e.target.value })} required />
          <input className="border rounded-lg px-3 py-2" placeholder={t("GEO_OFFICE_NETWORK")} value={officeForm.network} onChange={(e) => setOfficeForm({ ...officeForm, network: e.target.value })} required />
          <select className="border rounded-lg px-3 py-2" value={officeForm.countryId} onChange={(e) => { const country = countries.find((c) => c._id === e.target.value); setOfficeForm({ ...officeForm, countryId: e.target.value, countryName: country?.name || "", countryCode: country?.countryCode || "", stateId: "", stateName: "", stateCode: "" }); }}>
            <option value="">{t("GEO_SELECT_COUNTRY")}</option>
            {countries.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
          <select className="border rounded-lg px-3 py-2" value={officeForm.stateId} onChange={(e) => { const state = states.find((s) => s._id === e.target.value); setOfficeForm({ ...officeForm, stateId: e.target.value, stateName: state?.name || "", stateCode: state?.code || state?.abbreviation || "" }); }}>
            <option value="">{t("GEO_SELECT_STATE")}</option>
            {states.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
          </select>
          <input className="border rounded-lg px-3 py-2" placeholder={t("GEO_CITY")} value={officeForm.city} onChange={(e) => setOfficeForm({ ...officeForm, city: e.target.value })} />
          <input className="border rounded-lg px-3 py-2" placeholder={t("GEO_NOTES")} value={officeForm.notes} onChange={(e) => setOfficeForm({ ...officeForm, notes: e.target.value })} />
          <div className="md:col-span-3">
            <button type="submit" className="bg-LightBlue text-white px-5 py-2 rounded-lg">{editingOfficeId ? t("GEO_UPDATE_OFFICE") : t("GEO_ADD_OFFICE")}</button>
            {editingOfficeId && <button type="button" className="ml-2 border px-4 py-2 rounded-lg" onClick={() => { setEditingOfficeId(null); setOfficeForm(emptyOffice); }}>{t("CANCEL")}</button>}
          </div>
        </form>
        <IpTable rows={offices.map((o) => ({ value: o.network, label: [o.name, o.city, o.countryName].filter(Boolean).join(" — "), _id: o._id, raw: o }))} onEdit={(row) => { setEditingOfficeId(row._id); setOfficeForm({ name: row.raw.name || "", network: row.raw.network || "", countryId: "", countryName: row.raw.countryName || "", countryCode: row.raw.countryCode || "", stateId: "", stateName: row.raw.stateName || "", stateCode: row.raw.stateCode || "", city: row.raw.city || "", notes: row.raw.notes || "" }); }} onRemove={(row) => setRemoveConfirm({ name: row.label || row.value, run: () => removeOffice(row._id) })} empty={t("GEO_NO_OFFICES")} />
      </section> */}




      {ROLE_SECTIONS.map((section) => (
        <RoleIpSection key={section.role} title={t(section.titleKey)} help={t(section.helpKey)} rows={ipsFor(section.role)} draft={ipDrafts[section.role] || { value: "", label: "" }} onDraftChange={(next) => setIpDrafts((prev) => ({ ...prev, [section.role]: next }))} currentIp={mapLoc?.ip || policy.currentIp} onAdd={() => { const draft = ipDrafts[section.role] || {}; addRoleIp(section.role, draft.value, draft.label); setIpDrafts((prev) => ({ ...prev, [section.role]: { value: "", label: "" } })); }} onBlock={() => { const draft = ipDrafts[section.role] || {}; blockRoleIp(section.role, draft.value, draft.label); setIpDrafts((prev) => ({ ...prev, [section.role]: { value: "", label: "" } })); }} onAllowCurrent={() => addRoleIp(section.role, mapLoc?.ip || policy.currentIp, t("GEO_THIS_COMPUTER"))} onRemove={(value) => setRemoveConfirm({ name: value, run: () => removeRoleIp(section.role, value) })} hideEmpty t={t} />
      ))}
      <div className="mb-8">
        <Toggle
          checked={!!policy.enabled}
          disabled={!hasCompanyAdminIp && !policy.enabled}
          label={
            <span>
              <strong>{t("GEO_ENABLE_COMPANY")}</strong>
              <span className="block text-gray-500">{t("GEO_ENABLE_COMPANY_HINT")}</span>
            </span>
          }
          onChange={onToggleRestriction}
        />
        {!hasCompanyAdminIp && !policy.enabled ? (
          <p className="text-sm text-gray-500 mt-2">Add a company admin IP first, then you can enable restriction.</p>
        ) : null}
      </div>
      {ipsFor("any").length > 0 && <RoleIpSection title={t("GEO_SECTION_SHARED")} help={t("GEO_SECTION_SHARED_HELP")} rows={ipsFor("any")} draft={{ value: "", label: "" }} onDraftChange={() => {}} currentIp={null} onAdd={() => {}} onBlock={() => {}} onAllowCurrent={() => {}} onRemove={(value) => setRemoveConfirm({ name: value, run: () => removeRoleIp("any", value) })} hideAdd t={t} />}
      {/* PREVIOUS: blocked IPs section was hidden.
      <section className="mb-8">
        <h4 className="font-semibold mb-1">{t("GEO_SECTION_BLOCKED")}</h4>
      </section>
      */}
      <section className="mb-8">
        <h4 className="font-semibold mb-1">{t("GEO_SECTION_BLOCKED")}</h4>
        <p className="text-sm text-gray-500 mb-3">{t("GEO_SECTION_BLOCKED_HELP")}</p>
        <div className="flex flex-wrap gap-2 mb-3">
          <input className="border rounded-lg px-3 py-2" placeholder={t("GEO_IP_PLACEHOLDER")} value={blockValue} onChange={(e) => setBlockValue(e.target.value)} />
          <input className="border rounded-lg px-3 py-2" placeholder={t("GEO_LABEL")} value={blockLabel} onChange={(e) => setBlockLabel(e.target.value)} />
          <button type="button" className="border border-red-400 text-red-600 px-4 py-2 rounded-lg" onClick={() => { blockRoleIp("employee", blockValue, blockLabel); setBlockValue(""); setBlockLabel(""); }}>{t("GEO_BLOCK_IP")}</button>
        </div>
        <IpTable rows={policy.ipBlocklist || []} onRemove={(row) => setRemoveConfirm({ name: row.value, run: () => removeBlockedIp(row.value) })} empty={t("GEO_NONE")} />
      </section>


      <section>
        <h4 className="font-semibold mb-3">{t("GEO_TAB_LOGS")}</h4>
        <div className="overflow-x-auto relative rounded-lg border border-[#E1E6EE]">
          <table className="min-w-[980px] w-full text-xs text-left text-[#6c6c6c] border-collapse">
            <thead className="text-xs text-gray-900 bg-[#E1E6EE]">
              <tr>
                <th className="py-3 px-4 whitespace-nowrap border-r border-[#D5DCE8]">{t("GEO_TIME")}</th>
                <th className="py-3 px-4 whitespace-nowrap border-r border-[#D5DCE8]">{t("GEO_EMPLOYEE_ID")}</th>
                <th className="py-3 px-4 whitespace-nowrap border-r border-[#D5DCE8]">{t("GEO_EMAIL")}</th>
                <th className="py-3 px-4 whitespace-nowrap border-r border-[#D5DCE8]">{t("GEO_ROLE")}</th>
                <th className="py-3 px-4 whitespace-nowrap border-r border-[#D5DCE8]">{t("GEO_IP")}</th>
                <th className="py-3 px-4 whitespace-nowrap border-r border-[#D5DCE8]">{t("GEO_LOCATION")}</th>
                <th className="py-3 px-4 whitespace-nowrap">{t("GEO_DECISION")}</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => {
                const gps = String(log?.loginAddress || "").trim();
                const fromIp = [log?.city, log?.regionName, log?.countryName]
                  .filter(Boolean)
                  .join(", ");
                const locationText =
                  gps && fromIp && gps !== fromIp
                    ? `${gps} (${fromIp})`
                    : gps || fromIp || "-";
                return (
                  <tr key={log._id} className="bg-white border-b border-[#E9EDF9] last:border-b-0 align-top">
                    <td className="py-3 px-4 whitespace-nowrap border-r border-[#E9EDF9] text-gray-900">
                      {dayjs(log.createdAt).format("MM-DD-YYYY")}
                      <div className="text-[#8A8A8A]">{dayjs(log.createdAt).format("h:mm A")}</div>
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap border-r border-[#E9EDF9]">{log.employeeId || "-"}</td>
                    <td className="py-3 px-4 border-r border-[#E9EDF9] text-gray-900 break-all min-w-[180px]">{log.email || "-"}</td>
                    <td className="py-3 px-4 whitespace-nowrap border-r border-[#E9EDF9]">{log.actorRole || "-"}</td>
                    <td className="py-3 px-4 whitespace-nowrap border-r border-[#E9EDF9] font-mono">{log.ip || "-"}</td>
                    <td className="py-3 px-4 border-r border-[#E9EDF9] min-w-[220px] max-w-[320px] whitespace-normal break-words">{locationText}</td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <span className={`inline-block px-2 py-1 rounded font-medium ${log.decision === "DENY" ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>
                        {log.decision || "-"}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {logs.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-6 px-4 text-center text-gray-400">{t("GEO_NONE")}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex gap-2 mt-3">
          <button type="button" className="border px-3 py-1 rounded" disabled={logPage <= 1} onClick={() => loadLogs(logPage - 1)}>{t("GEO_PREV")}</button>
          <span className="text-sm py-1">{t("GEO_PAGE")} {logPage} / {logTotalPages}</span>
          <button type="button" className="border px-3 py-1 rounded" disabled={logPage >= logTotalPages} onClick={() => loadLogs(logPage + 1)}>{t("GEO_NEXT")}</button>
        </div>
      </section>
      {saving && <div className="mt-3 text-xs text-gray-400">{t("GEO_SAVING")}</div>}
      <ConfirmationModal
        isOpen={!!enableConfirm}
        onClose={() => setEnableConfirm(null)}
        onConfirm={() => {
          enableConfirm?.run?.();
          setEnableConfirm(null);
        }}
        title={enableConfirm?.title || "Confirm?"}
        message={enableConfirm?.message || ""}
        confirmText={enableConfirm?.confirmText || "Confirm"}
        cancelText={enableConfirm?.hideCancel ? "Close" : "Cancel"}
      />
      <ConfirmationModal
        isOpen={!!removeConfirm}
        onClose={() => setRemoveConfirm(null)}
        onConfirm={() => {
          removeConfirm?.run?.();
          setRemoveConfirm(null);
        }}
        isDanger
        title="Remove?"
        message={`Do you still want to remove ${removeConfirm?.name || "this"}?`}
        confirmText="Yes, remove"
        cancelText="Cancel"
      />
    </div>
  );
};

const RoleIpSection = ({ title, help, rows, draft, onDraftChange, currentIp, onAdd, onBlock, onAllowCurrent, onRemove, hideAdd, hideEmpty, t }) => (
  <section className="mb-8">
    <h4 className="font-semibold mb-1">{title}</h4>
    <p className="text-sm text-gray-500 mb-3">{help}</p>
    {!hideAdd && (
      <div className="flex flex-wrap gap-2 mb-3">
        <input className="border rounded-lg px-3 py-2" placeholder={t("GEO_IP_PLACEHOLDER")} value={draft.value} onChange={(e) => onDraftChange({ ...draft, value: e.target.value })} />
        <input className="border rounded-lg px-3 py-2" placeholder={t("GEO_LABEL")} value={draft.label} onChange={(e) => onDraftChange({ ...draft, label: e.target.value })} />
        <button type="button" className="bg-LightBlue text-white px-4 py-2 rounded-lg" onClick={onAdd}>{t("GEO_ADD_IP")}</button>
        <button type="button" className="border border-red-400 text-red-600 px-4 py-2 rounded-lg" onClick={onBlock}>{t("GEO_BLOCK_IP")}</button>
        {currentIp && <button type="button" className="border px-4 py-2 rounded-lg" onClick={onAllowCurrent}>{t("GEO_ALLOW_MY_IP")}</button>}
      </div>
    )}
    <IpTable rows={rows} onRemove={(row) => onRemove(row.value)} empty={hideEmpty ? "" : t("GEO_NONE")} />
  </section>
);

const IpTable = ({ rows, onRemove, onAllow, onEdit, empty }) => {
  const list = rows || [];
  if (!list.length) {
    if (!empty) return null;
    return (
      <div className="border rounded-lg overflow-hidden">
        <div className="p-3 text-sm text-gray-400">{empty}</div>
      </div>
    );
  }
  return (
    <div className="border rounded-lg overflow-hidden">
      {list.map((row) => (
        <div key={row._id || `${row.role || ""}-${row.value}`} className="flex justify-between items-center px-3 py-2 border-b last:border-b-0 text-sm">
          <span>{row.value} {row.label ? `— ${row.label}` : ""}</span>
          <div className="flex gap-3">
            {onEdit && <button type="button" className="text-blue-600" onClick={() => onEdit(row)}>Edit</button>}
            {onAllow && <button type="button" className="text-blue-600" onClick={() => onAllow(row)}>Allow</button>}
            {onRemove && <button type="button" className="text-red-500" onClick={() => onRemove(row)}>Remove</button>}
          </div>
        </div>
      ))}
    </div>
  );
};

const Toggle = ({ checked, label, onChange, disabled }) => (
  <div className="flex items-start gap-3 text-sm">
    <button
      type="button"
      role="switch"
      aria-checked={!!checked}
      aria-disabled={!!disabled}
      disabled={!!disabled}
      onClick={() => {
        if (disabled) return;
        onChange();
      }}
      className={`relative mt-0.5 inline-flex h-6 w-11 shrink-0 rounded-full border-0 transition-colors ${
        checked ? "bg-LightBlue" : "bg-gray-300"
      } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 mt-0.5 rounded-full bg-white shadow transform transition-transform ${
          checked ? "translate-x-5" : "translate-x-0.5"
        }`}
      />
    </button>
    <span>{label}</span>
  </div>
);

export default GeographicAccess;